import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Category } from '../api';
import { CategoryIcon } from '../categoryIcons';

type Props = {
  id: string;
  categories: Category[];
  value: string;
  onChange: (categoryId: string) => void;
  /** Mostra la prima voce vuota (es. modale “Nuovo”). */
  allowEmpty?: boolean;
};

export function CategoryPicker({ id, categories, value, onChange, allowEmpty }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = categories.find((c) => c.categoryId === value);

  return (
    <div className="category-picker" ref={rootRef}>
      <button
        type="button"
        id={id}
        className="category-picker-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {selected ? (
          <>
            <CategoryIcon iconId={selected.icon} size={22} className="category-picker-trigger-icon" />
            <span className="category-picker-trigger-label">{selected.name}</span>
            <span className={`category-picker-pill category-picker-pill--${selected.type}`}>
              {selected.type === 'in' ? 'Entrata' : 'Uscita'}
            </span>
          </>
        ) : (
          <span className="category-picker-placeholder">Scegli una categoria…</span>
        )}
        <ChevronDown
          size={18}
          strokeWidth={2}
          className={`category-picker-chevron ${open ? 'category-picker-chevron--open' : ''}`}
          aria-hidden
        />
      </button>
      {open && (
        <div className="category-picker-panel" role="listbox" aria-labelledby={id}>
          {allowEmpty && (
            <button
              type="button"
              role="option"
              aria-selected={value === ''}
              className={`category-picker-option ${value === '' ? 'selected' : ''}`}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              <span className="category-picker-option-name category-picker-option-name--muted">—</span>
            </button>
          )}
          {categories.map((c) => (
            <button
              key={c.categoryId}
              type="button"
              role="option"
              aria-selected={value === c.categoryId}
              className={`category-picker-option ${value === c.categoryId ? 'selected' : ''}`}
              onClick={() => {
                onChange(c.categoryId);
                setOpen(false);
              }}
            >
              <CategoryIcon iconId={c.icon} size={22} className="category-picker-option-icon" />
              <span className="category-picker-option-name">{c.name}</span>
              <span className={`category-picker-pill category-picker-pill--${c.type}`}>
                {c.type === 'in' ? 'Entrata' : 'Uscita'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
